import React, { useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faRobot, faBalanceScale, faShieldAlt, faUsers, faBrain, faEye } from '@fortawesome/free-solid-svg-icons';

const AIEthicsPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="policy-page">
      
      <Container className="py-5">
        <Row>
          <Col lg={10} className="mx-auto">
            <Link to="/" className="btn btn-link mb-4">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </Link>
            
            <Card className="policy-content">
              <Card.Body className="p-5">
                <div className="text-center mb-5">
                  <FontAwesomeIcon icon={faRobot} size="3x" className="text-primary mb-3" />
                  <h1>AI Ethics and Usage Policy</h1>
                  <p className="text-muted">Last Updated: {new Date().toLocaleDateString()}</p>
                </div>

                <div className="policy-section">
                  <h2>Our Commitment to Ethical AI</h2>
                  <p>
                    At KairosIQ AI Realty, powered by KairosIQ AI technology, we believe that artificial 
                    intelligence should enhance human decision-making, not replace it. This policy 
                    outlines our commitment to responsible AI usage in real estate services, ensuring 
                    fairness, transparency, and accountability in every interaction.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>1. Core AI Ethics Principles</h3>
                  
                  <div className="ethics-principle">
                    <h4><FontAwesomeIcon icon={faBalanceScale} /> Fairness and Non-Discrimination</h4>
                    <p>
                      Our AI systems are designed and regularly audited to prevent discrimination 
                      based on race, color, religion, sex, national origin, disability, or familial 
                      status. We actively work to identify and eliminate bias in our algorithms.
                    </p>
                    <ul>
                      <li>Regular bias testing and algorithmic audits</li>
                      <li>Diverse training data that represents all communities</li>
                      <li>Fair Housing Act compliance built into every recommendation</li>
                      <li>Continuous monitoring for discriminatory patterns</li>
                    </ul>
                  </div>

                  <div className="ethics-principle">
                    <h4><FontAwesomeIcon icon={faEye} /> Transparency</h4>
                    <p>
                      We believe you have the right to understand how AI influences your property 
                      search experience.
                    </p>
                    <ul>
                      <li>Clear labeling when AI is making recommendations</li>
                      <li>Explanations available for why properties are suggested</li>
                      <li>Disclosure of factors considered in matching algorithms</li>
                      <li>Open about AI limitations and capabilities</li>
                    </ul>
                  </div>

                  <div className="ethics-principle">
                    <h4><FontAwesomeIcon icon={faShieldAlt} /> Privacy and Security</h4>
                    <p>
                      Your data is protected with industry-leading security measures, and we minimize 
                      data collection to what's necessary for providing our services.
                    </p>
                    <ul>
                      <li>End-to-end encryption for sensitive data</li>
                      <li>Data minimization - we only collect what we need</li>
                      <li>No selling of personal data to third parties</li>
                      <li>Regular security audits and updates</li>
                    </ul>
                  </div>

                  <div className="ethics-principle">
                    <h4><FontAwesomeIcon icon={faUsers} /> Human Oversight</h4>
                    <p>
                      While AI enhances our services, licensed real estate professionals always 
                      maintain ultimate decision-making authority.
                    </p>
                    <ul>
                      <li>All transactions supervised by licensed agents</li>
                      <li>AI recommendations can be overridden by human judgment</li>
                      <li>Direct agent contact available at any time</li>
                      <li>Critical decisions require human verification</li>
                    </ul>
                  </div>
                </div>

                <div className="policy-section">
                  <h3>2. How Our AI Works</h3>
                  
                  <h4>Property Matching</h4>
                  <p>
                    KairosIQ AI analyzes your preferences, search behavior, and stated requirements 
                    to suggest properties that match your needs. The system considers:
                  </p>
                  <ul>
                    <li>Your budget and financial parameters</li>
                    <li>Location preferences and commute requirements</li>
                    <li>Property features and amenities</li>
                    <li>Market trends and value assessments</li>
                    <li>Similar successful matches for comparable users</li>
                  </ul>

                  <h4>Natural Language Understanding</h4>
                  <p>
                    Our chat interface uses natural language processing to understand your queries 
                    in plain English, making property search more intuitive and accessible.
                  </p>

                  <h4>Predictive Analytics</h4>
                  <p>
                    We use historical data and market trends to provide insights about property 
                    values, neighborhood developments, and investment potential.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>3. Your Rights and Control</h3>
                  
                  <p>You have complete control over how AI is used in your experience:</p>
                  <ul>
                    <li><strong>Opt-Out Rights:</strong> You can choose to search without AI assistance</li>
                    <li><strong>Explanation Rights:</strong> Request explanations for any AI recommendation</li>
                    <li><strong>Correction Rights:</strong> Report and correct any AI errors or biases</li>
                    <li><strong>Data Control:</strong> Manage what data AI can use for personalization</li>
                    <li><strong>Human Alternative:</strong> Always have access to human agents instead of AI</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>4. Bias Prevention and Fair Housing</h3>
                  
                  <p>
                    We take extensive measures to prevent bias and ensure Fair Housing compliance:
                  </p>
                  <ul>
                    <li><strong>Algorithmic Audits:</strong> Quarterly reviews by independent third parties</li>
                    <li><strong>Diverse Development Team:</strong> Our AI team reflects the communities we serve</li>
                    <li><strong>Inclusive Training Data:</strong> Representative samples from all neighborhoods</li>
                    <li><strong>Prohibited Factors:</strong> AI never considers protected characteristics</li>
                    <li><strong>Continuous Monitoring:</strong> Real-time detection of potential bias patterns</li>
                  </ul>

                  <div className="alert alert-info mt-3">
                    <strong>Report Concerns:</strong> If you believe our AI has exhibited bias or 
                    discrimination, please report it immediately to <a href="mailto:fairhousing@connectrealty.com">fairhousing@connectrealty.com</a>
                  </div>
                </div>

                <div className="policy-section">
                  <h3>5. Data Usage and Learning</h3>
                  
                  <h4>How AI Improves</h4>
                  <p>
                    Our AI systems learn from aggregated, anonymized user interactions to improve 
                    recommendations for everyone:
                  </p>
                  <ul>
                    <li>Search patterns help refine matching algorithms</li>
                    <li>Feedback improves accuracy of recommendations</li>
                    <li>Market data updates keep suggestions current</li>
                    <li>Error corrections prevent future mistakes</li>
                  </ul>

                  <h4>What We Don't Do</h4>
                  <ul>
                    <li>Create discriminatory user profiles</li>
                    <li>Share individual behavior with third parties</li>
                    <li>Use AI to manipulate or pressure decisions</li>
                    <li>Replace human judgment in critical situations</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>6. Accountability and Governance</h3>
                  
                  <p>
                    We maintain strict governance over our AI systems:
                  </p>
                  <ul>
                    <li><strong>Ethics Committee:</strong> Reviews all AI deployments and updates</li>
                    <li><strong>Regular Audits:</strong> Independent assessments of AI fairness</li>
                    <li><strong>Incident Response:</strong> Rapid investigation of any concerns</li>
                    <li><strong>Public Transparency:</strong> Annual reports on AI performance and fairness</li>
                    <li><strong>Continuous Education:</strong> Ongoing training for our team on AI ethics</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>7. Future Commitments</h3>
                  
                  <p>
                    As AI technology evolves, we commit to:
                  </p>
                  <ul>
                    <li>Staying current with best practices in AI ethics</li>
                    <li>Engaging with communities to understand concerns</li>
                    <li>Investing in bias prevention and fairness research</li>
                    <li>Maintaining human-centered design principles</li>
                    <li>Being transparent about new AI capabilities</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>8. Contact Us</h3>
                  <p>
                    For questions about our AI ethics, to report concerns, or to exercise your 
                    rights regarding AI usage:
                  </p>
                  <ul className="list-unstyled">
                    <li><strong>AI Ethics Team:</strong> <a href="mailto:ai-ethics@connectrealty.com">ai-ethics@connectrealty.com</a></li>
                    <li><strong>Fair Housing Concerns:</strong> <a href="mailto:fairhousing@connectrealty.com">fairhousing@connectrealty.com</a></li>
                    <li><strong>Phone:</strong> 1-602-860-6110</li>
                    <li><strong>Mail:</strong> KairosIQ AI Realty, AI Ethics Department, 410 N. Scottsdale Rd, Suite 10-160, Tempe, AZ 85281</li>
                  </ul>
                </div>

                <div className="text-center mt-5">
                  <Link to="/privacy-policy" className="btn btn-outline-primary me-2">
                    Privacy Policy
                  </Link>
                  <Link to="/fair-housing" className="btn btn-outline-primary">
                    Fair Housing Statement
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>


      <style>{`
        .policy-page {
          background-color: #f8f9fa;
          min-height: 100vh;
        }

        .policy-content {
          box-shadow: 0 0 20px rgba(0,0,0,0.1);
          border: none;
        }

        .policy-section {
          margin-bottom: 40px;
        }

        .policy-section h2 {
          color: #333;
          margin-bottom: 20px;
          font-size: 28px;
        }

        .policy-section h3 {
          color: #333;
          margin-bottom: 15px;
          margin-top: 30px;
          font-size: 22px;
        }

        .policy-section h4 {
          color: #555;
          margin-bottom: 10px;
          margin-top: 20px;
          font-size: 18px;
        }

        .policy-section p {
          line-height: 1.7;
          color: #555;
        }

        .policy-section ul {
          line-height: 1.8;
          color: #555;
        }

        .policy-section ul li {
          margin-bottom: 8px;
        }

        .ethics-principle {
          background-color: #f8f9fa;
          border-left: 4px solid #007bff;
          padding: 20px;
          margin-bottom: 20px;
          border-radius: 0 8px 8px 0;
        }

        .ethics-principle h4 {
          color: #007bff;
          margin-bottom: 15px;
        }

        .ethics-principle h4 svg {
          margin-right: 10px;
        }
      `}</style>
    </div>
  );
};

export default AIEthicsPolicy;